import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";

function StockPostingDialog({
  open,
  eyebrow,
  title,
  subtitle,
  action,
  actions,
  onActionChange,
  onClose,
  onSubmit,
  saving,
  cancelLabel,
  submitLabel,
  loadingLabel,
  closeLabel,
  dirty = false,
  dirtyLabel,
  children,
}) {
  const formRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      const firstLogicalField = formRef.current?.querySelector(
        '.stock-dialog__body input:not([disabled]):not([type="hidden"]):not([readonly]), .stock-dialog__body select:not([disabled]), .stock-dialog__body textarea:not([disabled])'
      );
      firstLogicalField?.focus();
      firstLogicalField?.select?.();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, saving]);

  if (!open) return null;

  return createPortal(
    <div
      className="stock-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <FocusLock returnFocus>
        <form
          ref={formRef}
          className="stock-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stock-posting-dialog-title"
          onSubmit={onSubmit}
        >
          <header className="stock-dialog__header">
            <div>
              <span className="stock-dialog__eyebrow">{eyebrow}</span>
              <h2 id="stock-posting-dialog-title">{title}</h2>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <button
              type="button"
              className="stock-dialog__close"
              onClick={onClose}
              disabled={saving}
              aria-label={closeLabel}
              title={closeLabel}
            >
              ×
            </button>
          </header>

          <div className="stock-dialog__action-bar" role="tablist" aria-label={eyebrow}>
            {actions.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={action === item.value}
                className={action === item.value ? "active" : ""}
                onClick={() => onActionChange(item.value)}
                disabled={saving}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="stock-dialog__body">
            <div className="stock-form stock-form--dialog">{children}</div>
          </div>

          <footer className="stock-dialog__footer">
            <div className="stock-dialog__footer-status" aria-live="polite">
              {dirty && dirtyLabel ? <span className="stock-dialog__dirty">{dirtyLabel}</span> : null}
            </div>
            <div className="stock-dialog__footer-actions">
            <button
              type="button"
              className="stock-btn stock-btn--secondary"
              onClick={onClose}
              disabled={saving}
            >
              {cancelLabel}
            </button>
            <button type="submit" className="stock-btn stock-btn--primary" disabled={saving}>
              {saving ? loadingLabel : submitLabel}
            </button>
            </div>
          </footer>
        </form>
      </FocusLock>
    </div>,
    document.body
  );
}

export default StockPostingDialog;
